import { useEffect, useMemo, useState } from 'react';
import { Search, CheckSquare, Square, ChevronLeft, ChevronRight, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { RecordCard } from '../components/RecordCard';
import { DiffDrawer } from '../components/DiffDrawer';
import { ButtonTemplate } from '../../../components/ui/buttons/ButtonTemplate';
import {
  useGetRecordsQuery,
  useCreateBatchMutation,
} from '../api/parserImportApi';

const PAGE_SIZE = 50;
const SKELETON_COUNT = 6;

// react-window was deliberately dropped here in favour of a plain CSS grid
// inside flex-1 min-h-0 overflow-y-auto (matching ParserView.jsx pattern).
// 50 items/page does not justify virtualization, and the fixed row height
// caused a truncated last-row artefact reported in production. Do not add
// react-window back to this component. (See HistoryDialog for the one place
// where the trade-off does make sense: it renders unbounded history rows.)
export const PreviewStep = ({ flow, token }) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('distance');
  const [search, setSearch] = useState('');
  const [diffRecord, setDiffRecord] = useState(null);

  const { data, isLoading, error, isFetching } = useGetRecordsQuery({
    token,
    radius_km: flow.radiusKm,
    filters: flow.filters,
    page,
    per_page: PAGE_SIZE,
    sort,
  });

  const [createBatch, { isLoading: isCreating }] = useCreateBatchMutation();

  const records = data?.data ?? [];
  const meta = data?.meta;

  // Client-side search on visible page — cheaper than a network round-trip
  // for the typical case where a user is narrowing 50 records.
  const filtered = useMemo(() => {
    if (!search.trim()) return records;
    const q = search.trim().toLowerCase();
    return records.filter(
      (r) =>
        r.title?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        String(r.parser_advert_id).includes(q)
    );
  }, [records, search]);

  // Auto-decide defaults on first load of a page: new -> create, changed -> enrich (default fields), unchanged -> skip.
  useEffect(() => {
    if (!records.length) return;
    const entries = records
      .filter((r) => !flow.decisions[r.parser_advert_id])
      .map((r) => {
        if (r.state === 'new') return [r.parser_advert_id, { mode: 'create' }];
        if (r.state === 'changed')
          return [
            r.parser_advert_id,
            {
              mode: 'enrich',
              selected_fields: ['price', 'photos', 'features', 'agency_phone'],
            },
          ];
        return [r.parser_advert_id, { mode: 'skip' }];
      });
    if (entries.length) flow.setDecisionsBulk(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const decisionCounts = useMemo(() => {
    let create = 0, enrich = 0, overwrite = 0, skip = 0;
    Object.values(flow.decisions).forEach((d) => {
      if (d?.mode === 'create') create += 1;
      else if (d?.mode === 'enrich') enrich += 1;
      else if (d?.mode === 'overwrite') overwrite += 1;
      else if (d?.mode === 'skip') skip += 1;
    });
    return { create, enrich, overwrite, skip };
  }, [flow.decisions]);

  const totalPicked = decisionCounts.create + decisionCounts.enrich + decisionCounts.overwrite;

  const bulkSet = (mode) => {
    const entries = filtered.map((r) => {
      const decision = { mode };
      if (mode === 'enrich') {
        decision.selected_fields = ['price', 'photos', 'features', 'agency_phone'];
      }
      return [r.parser_advert_id, decision];
    });
    flow.setDecisionsBulk(entries);
  };

  const startImport = async () => {
    if (totalPicked === 0) {
      toast.warning('Select at least one record to import');
      return;
    }
    try {
      const res = await createBatch({
        token,
        radius_km: flow.radiusKm,
        filters: flow.filters,
        records: flow.selectedRecords,
      }).unwrap();
      flow.setBatchId(res.batch_id);
      toast.success(`Batch #${res.batch_id} started (${res.total} records)`);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to start import');
    }
  };

  const totalPages = meta?.last_page ?? 1;
  const totalRecords = meta?.total ?? filtered.length;

  // Grid class shared by loading skeleton + loaded cards so both live in
  // the same clipping/scroll container.
  const gridClass =
    'grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <div className="flex flex-col gap-3 w-full max-h-[78vh]">
      <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-darkGrayColor"
            />
            <input
              type="text"
              placeholder="Search title, city, id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor w-64"
            />
          </div>

          <select
            data-testid="preview-sort-distance"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1.5 outline-none focus:border-purpleColor"
          >
            <option value="distance">Sort: distance</option>
            <option value="price_asc">Sort: price ↑</option>
            <option value="price_desc">Sort: price ↓</option>
            <option value="publication_date">Sort: newest</option>
          </select>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-darkGrayColor">Bulk:</span>
          <button
            type="button"
            data-testid="preview-select-all"
            onClick={() => bulkSet('create')}
            className="flex items-center gap-1 px-2 py-1 rounded border border-greenColor text-greenColor hover:bg-greenColor/10"
          >
            <CheckSquare size={12} /> Create all
          </button>
          <button
            type="button"
            onClick={() => bulkSet('enrich')}
            className="flex items-center gap-1 px-2 py-1 rounded border border-purpleColor text-purpleColor hover:bg-purpleColor/10"
          >
            <CheckSquare size={12} /> Enrich all
          </button>
          <button
            type="button"
            onClick={() => bulkSet('skip')}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 text-darkGrayColor hover:bg-lightGrayColor"
          >
            <Square size={12} /> Skip all
          </button>
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex items-center gap-3 flex-wrap text-xs flex-shrink-0">
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-greenColor/15 text-greenColor">
          <Sparkles size={12} /> Create · {decisionCounts.create}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-purpleColor/15 text-purpleColor">
          <CheckCircle2 size={12} /> Enrich · {decisionCounts.enrich}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-orangeColor/15 text-orangeColor">
          <AlertTriangle size={12} /> Overwrite · {decisionCounts.overwrite}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-lightGrayColor text-darkGrayColor">
          <Square size={12} /> Skip · {decisionCounts.skip}
        </span>
        <span className="ml-auto text-darkGrayColor">
          Showing page {meta?.current_page ?? 1} of {totalPages} · {totalRecords} total
        </span>
      </div>

      <div
        data-testid="preview-record-list"
        className="w-full flex-1 min-h-0 overflow-y-auto pr-1"
      >
        {isLoading && (
          <div className={gridClass}>
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div
                key={i}
                className="w-full h-[340px] rounded-lg border border-gray-200 bg-lightGrayColor animate-pulse"
              />
            ))}
          </div>
        )}
        {!isLoading && error && (
          <div className="flex items-center justify-center h-full text-sm text-redColor">
            Failed to load records
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-darkGrayColor">
            No records match your search
          </div>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <div className={gridClass}>
            {filtered.map((rec) => (
              <RecordCard
                key={rec.parser_advert_id}
                record={rec}
                decision={flow.decisions[rec.parser_advert_id]}
                onDecisionChange={flow.setDecision}
                onOpenDiff={setDiffRecord}
              />
            ))}
          </div>
        )}
        {isFetching && !isLoading && (
          <div className="text-xs text-darkGrayColor mt-2">refreshing…</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs flex-shrink-0">
          <button
            type="button"
            data-testid="preview-pagination-prev"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
          >
            <ChevronLeft size={12} /> Prev
          </button>
          <span className="text-darkGrayColor">
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            data-testid="preview-pagination-next"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
          >
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between w-full pt-3 border-t border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={flow.prev}
          className="px-3 py-1.5 text-sm text-darkGrayColor rounded border border-gray-200 hover:bg-lightGrayColor"
        >
          ← Back to scope
        </button>
        <span data-testid="preview-create-batch">
          <ButtonTemplate
            text={
              totalPicked
                ? `Start import (${totalPicked} record${totalPicked > 1 ? 's' : ''}) →`
                : 'Start import'
            }
            bgColor="bg-purpleColor border-purpleColor"
            disabled={totalPicked === 0 || isCreating}
            isLoadingData={isCreating}
            handleClick={startImport}
          />
        </span>
      </div>

      {/* Diff drawer */}
      {diffRecord && (
        <DiffDrawer
          record={diffRecord}
          decision={flow.decisions[diffRecord.parser_advert_id]}
          token={token}
          onClose={() => setDiffRecord(null)}
          onSave={(decision) => {
            flow.setDecision(diffRecord.parser_advert_id, decision);
            setDiffRecord(null);
          }}
        />
      )}
    </div>
  );
};
